// ============================================================
// THE COMPANY — BoardView Component (full-screen overlay)
// ============================================================

import { useState, useMemo, useCallback } from "react";
import { useCompanyStore } from "../../store/useCompanyStore";
import { generateBoardReport } from "../../lib/promptTemplates";
import Modal from "../shared/Modal";
import StatusBadge from "../shared/StatusBadge";
import CopyButton from "../shared/CopyButton";
import DecisionLog from "./DecisionLog";
import KnowledgeGaps from "./KnowledgeGaps";

type Tab = "overview" | "decisions" | "gaps";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "decisions", label: "Decisions" },
  { id: "gaps", label: "Knowledge Gaps" },
];

export default function BoardView() {
  const boardViewOpen = useCompanyStore((s) => s.boardViewOpen);
  const toggleBoardView = useCompanyStore((s) => s.toggleBoardView);
  const roles = useCompanyStore((s) => s.roles);
  const decisions = useCompanyStore((s) => s.decisions);
  const conversations = useCompanyStore((s) => s.conversations);
  const actionItems = useCompanyStore((s) => s.actionItems);
  const selectRole = useCompanyStore((s) => s.selectRole);

  const [activeTab, setActiveTab] = useState<Tab>("overview");

  // Build the awareness map for generateBoardReport
  const decisionAwareness = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const d of decisions) {
      map[d.id] = d.informedRoleIds;
    }
    return map;
  }, [decisions]);

  // Generate the board report text
  const boardReportText = useMemo(
    () => generateBoardReport(roles, decisions, decisionAwareness),
    [roles, decisions, decisionAwareness],
  );

  // Handle clicking a role card — close board view and select the role
  const handleRoleClick = useCallback(
    (roleId: string) => {
      toggleBoardView();
      selectRole(roleId);
    },
    [toggleBoardView, selectRole],
  );

  // Count knowledge gaps for the tab badge
  const gapCount = useMemo(() => {
    let count = 0;
    for (const role of roles) {
      for (const d of decisions) {
        if (d.roleId !== role.id && !d.informedRoleIds.includes(role.id)) {
          count++;
        }
      }
    }
    return count;
  }, [roles, decisions]);

  return (
    <Modal
      isOpen={boardViewOpen}
      onClose={toggleBoardView}
      title="Board of Directors"
      width="900px"
    >
      {/* Tabs */}
      <div
        className="flex items-center gap-1 mb-5 rounded-lg p-1"
        style={{ backgroundColor: "var(--bg-base)" }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`
              relative flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-medium
              transition-all duration-150
              ${
                activeTab === tab.id
                  ? "text-white shadow-sm"
                  : "hover:text-[var(--text-primary)]"
              }
            `}
            style={{
              backgroundColor:
                activeTab === tab.id ? "var(--accent)" : "transparent",
              color: activeTab === tab.id ? "#ffffff" : "var(--text-secondary)",
              boxShadow:
                activeTab === tab.id
                  ? "0 1px 4px rgba(99, 102, 241, 0.3)"
                  : "none",
            }}
          >
            {tab.label}
            {/* Badge for knowledge gaps */}
            {tab.id === "gaps" && gapCount > 0 && (
              <span
                className="inline-flex items-center justify-center h-4 min-w-[16px] rounded-full px-1 text-[10px] font-bold"
                style={{
                  backgroundColor:
                    activeTab === "gaps"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(239, 68, 68, 0.2)",
                  color: activeTab === "gaps" ? "#ffffff" : "#ef4444",
                }}
              >
                {gapCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div
        className="overflow-y-auto pr-1"
        style={{ maxHeight: "calc(80vh - 160px)" }}
      >
        {/* ============ Overview Tab ============ */}
        {activeTab === "overview" && (
          <div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {roles
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((role) => {
                  const convoCount = conversations[role.id]?.length ?? 0;
                  const pendingItems = (actionItems[role.id] ?? []).filter(
                    (i) => !i.completed,
                  ).length;

                  return (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => handleRoleClick(role.id)}
                      className="flex flex-col items-start gap-2 rounded-lg p-4 text-left transition-all duration-150 hover:ring-1 hover:ring-[var(--accent)] group"
                      style={{
                        backgroundColor: "rgba(26, 26, 36, 0.7)",
                        border: "1px solid var(--border-subtle)",
                        backdropFilter: "blur(8px)",
                        backgroundImage:
                          "linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, transparent 50%)",
                      }}
                    >
                      {/* Top row: status + counts */}
                      <div className="flex items-center justify-between w-full">
                        <StatusBadge status={role.status} showLabel size="sm" />
                        <div className="flex items-center gap-2">
                          {pendingItems > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] font-medium"
                              style={{ color: "var(--accent)" }}
                              title={`${pendingItems} pending action item${pendingItems > 1 ? "s" : ""}`}
                            >
                              <svg
                                className="h-3 w-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              {pendingItems}
                            </span>
                          )}
                          {convoCount > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] font-medium"
                              style={{ color: "var(--text-muted)" }}
                            >
                              <svg
                                className="h-3 w-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
                                />
                              </svg>
                              {convoCount}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Name + title */}
                      <div className="min-w-0">
                        <p
                          className="text-sm font-semibold truncate group-hover:text-[var(--accent-hover)] transition-colors duration-150"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {role.name}
                        </p>
                        <p
                          className="text-xs truncate mt-0.5"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          {role.title}
                        </p>
                      </div>
                    </button>
                  );
                })}
            </div>

            {roles.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16">
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  No roles created yet.
                </p>
              </div>
            )}

            {/* Export Board Report */}
            <div
              className="flex items-center justify-between mt-6 pt-4 border-t"
              style={{ borderColor: "var(--border-subtle)" }}
            >
              <div>
                <p
                  className="text-xs font-medium"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Export full board report
                </p>
                <p
                  className="text-[11px] mt-0.5"
                  style={{ color: "var(--text-muted)" }}
                >
                  Copy to clipboard for Claude Code analysis
                </p>
              </div>
              <CopyButton text={boardReportText} label="Export Board Report" />
            </div>
          </div>
        )}

        {/* ============ Decisions Tab ============ */}
        {activeTab === "decisions" && <DecisionLog />}

        {/* ============ Knowledge Gaps Tab ============ */}
        {activeTab === "gaps" && <KnowledgeGaps />}
      </div>
    </Modal>
  );
}
